/**
 * Staff portal — Employee Dashboard.
 *
 * Single landing page that answers "what do I need to do today?". Layout
 * mirrors the production/live arrangement:
 *   · Stats row: Leave Balances · Pending Requests · Years at Company · Important Dates
 *   · Three-column grid: (Team Leave + Policies) | Birthdays | (Profile + Quick Actions)
 *   · Full-width Announcements feed
 *   · Full-width recent leave requests table
 * Default landing for /app/members.
 */

import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient, useIsFetching } from '@tanstack/react-query';
import dayjs from 'dayjs';
import {
  Stack,
  Title,
  Text,
  Group,
  Badge,
  Card,
  SimpleGrid,
  Grid,
  Button,
  Avatar,
  ThemeIcon,
  Anchor,
  Table,
  ActionIcon,
} from '@mantine/core';
import { FadeIn } from '@/components/animations';
import { PageHelpButton } from '@/components/help';
import {
  IconCalendarTime,
  IconHourglassEmpty,
  IconAward,
  IconCalendarStats,
  IconBell,
  IconPlus,
  IconRefresh,
  IconCalendar,
} from '@tabler/icons-react';
import {
  useStaffPortalLeaveService,
  useStaffPortalPolicyAssignmentService,
  useStaffPortalProfileService,
  useStaffPortalAnnouncementService,
  useStaffPortalCalendarService,
  usePaySpaceSyncService,
  usePublicHolidayService,
} from '@actschurch/ccms-sdk';
import { useEnrichedAuth } from '@/hooks/useEnrichedAuth';
import { hrKeys } from '../utils/queryKeys';
import { greetingForHour, formatDate, initials } from '../utils/hrHelpers';
import { LEAVE_STATUS, POLICY_ASSIGNMENT_STATUS } from '../utils/hrConstants';
import {
  TeamLeavePanel,
  TeamBirthdaysPanel,
  ProfileSnapshotCard,
  QuickActionsPanel,
} from './components';

export default function EmployeeDashboardPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // Spin the header refresh button while any dashboard ('hr'-namespaced) query is refetching.
  const isFetching = useIsFetching({ predicate: (q) => q.queryKey[0] === 'hr' });

  const profileService = useStaffPortalProfileService();
  const leaveService = useStaffPortalLeaveService();
  const assignmentsService = useStaffPortalPolicyAssignmentService();
  const announcementsService = useStaffPortalAnnouncementService();
  const calendarService = useStaffPortalCalendarService();
  const paySpaceSync = usePaySpaceSyncService();
  const publicHolidayService = usePublicHolidayService();
  const { user: authUser } = useEnrichedAuth();

  const { data: profile } = useQuery({
    queryKey: hrKeys.myProfile(),
    queryFn: () => profileService.getProfile(),
  });

  const { data: todaysBirthdays = [] } = useQuery({
    queryKey: hrKeys.birthdays(undefined, true),
    queryFn: () => calendarService.getBirthdays(undefined, true),
  });

  const { data: myLeaves = [] } = useQuery({
    queryKey: hrKeys.myLeaves(),
    queryFn: () => leaveService.getMyLeaves(),
  });
  const pendingLeaves = useMemo(
    () => myLeaves.filter((l: any) => l.statusID === LEAVE_STATUS.PENDING),
    [myLeaves]
  );
  const recentLeaves = useMemo(
    () =>
      [...myLeaves]
        .sort((a: any, b: any) => (b.applicationDate ?? '').localeCompare(a.applicationDate ?? ''))
        .slice(0, 5),
    [myLeaves]
  );

  const { data: myAssignments = [] } = useQuery({
    queryKey: hrKeys.myAssignments(),
    queryFn: () => assignmentsService.getMyAssignments(),
  });
  const pendingPolicies = useMemo(
    () =>
      myAssignments.filter(
        (a: any) =>
          a.status === POLICY_ASSIGNMENT_STATUS.PENDING ||
          a.status === POLICY_ASSIGNMENT_STATUS.OVERDUE
      ),
    [myAssignments]
  );

  const userId = (profile as any)?.userId;
  const { data: balanceData } = useQuery({
    queryKey: userId ? ['hr', 'payspace', 'balances', userId] : ['hr', 'payspace', 'balances', 'noop'],
    queryFn: () => paySpaceSync.getLeaveBalances(userId!),
    enabled: !!userId,
    staleTime: 5 * 60 * 1000,
  });
  const totalLeaveDays = useMemo(() => {
    if (!balanceData?.isLinked) return null;
    return (balanceData.balances ?? []).reduce(
      (sum: number, b: any) => sum + (Number(b.availableDays) || 0),
      0
    );
  }, [balanceData]);

  const { data: announcements = [] } = useQuery({
    queryKey: hrKeys.myAnnouncements(10),
    queryFn: () => announcementsService.listActive(10),
  });

  const { data: upcomingHolidays = [] } = useQuery({
    queryKey: hrKeys.publicHolidays(true),
    queryFn: () => publicHolidayService.getUpcoming(5),
  });

  const yearsAtCompany = useMemo(() => {
    const start = (profile as any)?.startDate;
    if (!start) return null;
    const startMs = new Date(start).getTime();
    if (Number.isNaN(startMs)) return null;
    const years = (Date.now() - startMs) / (365.25 * 24 * 60 * 60 * 1000);
    return Math.floor(years * 10) / 10;
  }, [profile]);

  // Soonest upcoming personal date (work anniversary / birthday / probation /
  // contract expiry) — drives the "Important Dates" stat tile, same source as
  // the full ImportantDatesPanel.
  const nextImportantDate = useMemo(() => {
    const p: any = profile;
    if (!p) return null;
    const today = dayjs().startOf('day');
    const rows: { label: string; date: dayjs.Dayjs; days: number }[] = [];

    if (p.birthDate) {
      const dob = dayjs(p.birthDate);
      let next = dob.year(today.year());
      if (next.isBefore(today, 'day')) next = next.add(1, 'year');
      rows.push({ label: 'Birthday', date: next, days: next.diff(today, 'day') });
    }
    if (p.startDate) {
      const start = dayjs(p.startDate);
      let anniv = start.year(today.year());
      if (anniv.isBefore(today, 'day')) anniv = anniv.add(1, 'year');
      rows.push({ label: 'Work Anniversary', date: anniv, days: anniv.diff(today, 'day') });
      const prob = start.add(90, 'day');
      if (prob.isAfter(today, 'day')) {
        rows.push({ label: 'Probation End', date: prob, days: prob.diff(today, 'day') });
      }
    }
    if (p.endDate) {
      const end = dayjs(p.endDate);
      if (!end.isBefore(today, 'day')) {
        rows.push({ label: 'Contract Expiry', date: end, days: end.diff(today, 'day') });
      }
    }
    if (rows.length === 0) return null;
    rows.sort((a, b) => a.days - b.days);
    return rows[0];
  }, [profile]);

  const greeting = useMemo(() => greetingForHour(new Date()), []);
  // The profile payload exposes `fullName`/`name`/`surname` (not `firstName`); fall back to the
  // authenticated user (same source ProfileSnapshotCard uses) so the header never renders blank.
  const fullName =
    (profile as any)?.fullName ||
    authUser?.displayName ||
    `${(profile as any)?.name ?? ''} ${(profile as any)?.surname ?? ''}`.trim();
  const greetingName = (profile as any)?.name || (fullName ? fullName.split(' ')[0] : '');
  const avatarSrc = (profile as any)?.avatar || authUser?.avatar || undefined;
  const isMyBirthday = useMemo(() => {
    if (!profile || !(profile as any).userId) return false;
    return todaysBirthdays.some(
      (b: any) =>
        (b.firstName ?? '').toLowerCase() === (greetingName as string).toLowerCase()
    );
  }, [todaysBirthdays, profile, greetingName]);

  return (
    <FadeIn>
      <Stack gap="md" p="md" maw={1280} mx="auto">
        {/* Greeting row */}
        <Group justify="space-between" align="flex-start">
          <Group gap="md">
            <Avatar src={avatarSrc} size={56} radius="xl">
              {initials(fullName || greetingName)}
            </Avatar>
            <Stack gap={2}>
              <Title order={2}>
                {greeting}{greetingName ? `, ${greetingName}` : ''}
              </Title>
              <Text size="sm" c="dimmed">
                {isMyBirthday ? '🎂 Happy birthday! ' : ''}
                {(profile as any)?.jobTitle ?? ''}
              </Text>
            </Stack>
          </Group>
          <Group gap="sm" data-help-id="hr.dashboard.actions">
            <ActionIcon
              variant="light"
              color="indigo"
              radius="xl"
              size="lg"
              loading={isFetching > 0}
              onClick={() => queryClient.invalidateQueries({ predicate: (q) => q.queryKey[0] === 'hr' })}
              aria-label="Refresh dashboard"
            >
              <IconRefresh size={18} />
            </ActionIcon>
            <PageHelpButton pageKey="hr.dashboard" />
          </Group>
        </Group>

        {/* Top stats */}
        <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }} spacing="md" data-help-id="hr.dashboard.stats">
          <InfoTile
            label="Leave Balances"
            icon={<IconCalendarTime size={20} />}
            color="teal"
            onClick={() => navigate('/app/members/my-leaves')}
            primary={totalLeaveDays != null ? `${totalLeaveDays.toFixed(1)} days available` : undefined}
            secondary={
              balanceData?.isLinked === false || totalLeaveDays == null
                ? 'Not linked to PaySpace — contact HR.'
                : undefined
            }
          />
          <StatTile
            label="Pending Requests"
            value={String(pendingLeaves.length)}
            icon={<IconHourglassEmpty size={20} />}
            color="yellow"
            onClick={() => navigate('/app/members/my-leaves')}
          />
          <StatTile
            label="Years at Company"
            value={yearsAtCompany != null ? yearsAtCompany.toFixed(0) : '—'}
            icon={<IconAward size={20} />}
            color="grape"
            onClick={() => navigate('/app/members/my-profile')}
          />
          <InfoTile
            label="Important Dates"
            icon={<IconCalendarStats size={20} />}
            color="indigo"
            onClick={() => navigate('/app/members/my-profile')}
            primary={nextImportantDate?.label}
            secondary={
              nextImportantDate ? formatDate(nextImportantDate.date.toDate()) : 'No upcoming dates'
            }
            badge={
              nextImportantDate
                ? nextImportantDate.days === 0
                  ? 'Today'
                  : `${nextImportantDate.days}D`
                : undefined
            }
          />
        </SimpleGrid>

        {/* Body: left 2/3 holds the (team leave + policies | birthdays) sub-row, then
            Announcements + Recent Leave Requests beneath them. Right 1/3 holds profile + quick
            actions. Splitting the 8-wide left column into two 6s keeps Team Leave / Birthdays /
            Profile each reading as a third of the page. */}
        <Grid gutter="md">
          <Grid.Col span={{ base: 12, md: 8 }}>
            <Stack gap="md">
              {/* Top sub-row: (team leave + policies) | birthdays */}
              <Grid gutter="md">
                <Grid.Col span={{ base: 12, sm: 6 }}>
                  {/* Team leave + policies fill the column height to match the tall Birthdays card. */}
                  <Stack gap="md" h="100%">
                    {/* Team leave — teammates on leave now + starting soon */}
                    <div data-help-id="hr.dashboard.team-leave">
                      <TeamLeavePanel />
                    </div>

                    {/* Policies awaiting signature — flex:1 so the pair fills the column height */}
                    <Card withBorder padding="lg" radius="md" style={{ flex: 1 }} data-help-id="hr.dashboard.policies">
                      <Group justify="space-between" mb="sm">
                        <Title order={5}>Policies Awaiting Signature</Title>
                        <Anchor size="sm" onClick={() => navigate('/app/members/my-policies')}>View all</Anchor>
                      </Group>
                      {pendingPolicies.length === 0 ? (
                        <Text size="sm" c="dimmed">You're all signed up — nothing pending.</Text>
                      ) : (
                        <Stack gap="xs">
                          {pendingPolicies.slice(0, 5).map((a: any) => (
                            <Group
                              key={a.assignmentID}
                              justify="space-between"
                              wrap="nowrap"
                              style={{ cursor: 'pointer' }}
                              onClick={() => navigate(`/app/members/my-policies/${a.policyID}`)}
                            >
                              <Stack gap={0} style={{ minWidth: 0, flex: 1 }}>
                                <Text size="sm" fw={500} truncate="end">{a.policyName ?? 'Policy'}</Text>
                                <Text size="xs" c="dimmed">
                                  {a.dueDate ? `Due ${formatDate(a.dueDate)}` : 'No due date'}
                                </Text>
                              </Stack>
                              <Badge color={a.status === 'Overdue' ? 'red' : 'yellow'} variant="filled">
                                {a.status}
                              </Badge>
                            </Group>
                          ))}
                        </Stack>
                      )}
                    </Card>
                  </Stack>
                </Grid.Col>

                <Grid.Col span={{ base: 12, sm: 6 }} data-help-id="hr.dashboard.birthdays">
                  {/* Birthdays — today's celebrants + upcoming */}
                  <TeamBirthdaysPanel />
                </Grid.Col>
              </Grid>

              {/* Announcements — under policies + birthdays */}
              <Card withBorder padding="lg" radius="md" data-help-id="hr.dashboard.announcements">
                <Group justify="space-between" mb="sm">
                  <Group gap="xs">
                    <ThemeIcon variant="light" color="blue" size="sm" radius="xl">
                      <IconBell size={14} />
                    </ThemeIcon>
                    <Title order={4}>Announcements</Title>
                  </Group>
                  <Anchor size="sm" onClick={() => navigate('/app/members/announcements')}>View all</Anchor>
                </Group>
                {announcements.length === 0 ? (
                  <Text size="sm" c="dimmed" ta="center" py="xl">No active announcements.</Text>
                ) : (
                  <Stack gap="md">
                    {announcements.map((a: any) => (
                      <Stack key={a.announcementID} gap={4}>
                        <Group gap="xs" wrap="nowrap">
                          <Text fw={600} size="sm" style={{ flex: 1 }} truncate="end">
                            {a.title}
                          </Text>
                          {a.typeName && (
                            <Badge
                              size="sm"
                              variant="light"
                              color={a.typeColor ? undefined : 'blue'}
                              style={a.typeColor ? { backgroundColor: `${a.typeColor}1A`, color: a.typeColor } : undefined}
                            >
                              {a.typeName}
                            </Badge>
                          )}
                        </Group>
                        <Text size="xs" c="dimmed">{formatDate(a.publishedAt)}</Text>
                        {/* Body is HTML — render via dangerouslySetInnerHTML, clamped to 2 lines. */}
                        <div
                          style={{
                            fontSize: 'var(--mantine-font-size-sm)',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }}
                          dangerouslySetInnerHTML={{ __html: a.body ?? '' }}
                        />
                        {a.attachments?.length > 0 && (
                          <Text size="xs" c="dimmed">
                            📎 {a.attachments.length} attachment{a.attachments.length > 1 ? 's' : ''}
                          </Text>
                        )}
                      </Stack>
                    ))}
                  </Stack>
                )}
              </Card>

              {/* My recent leave requests — table, under policies + birthdays */}
              <Card withBorder padding="lg" radius="md" data-help-id="hr.dashboard.recent-leaves">
                <Group justify="space-between" mb="sm">
                  <Title order={4}>My Recent Leave Requests</Title>
                  <Group gap="xs">
                    <Button size="xs" variant="light" leftSection={<IconPlus size={14} />} onClick={() => navigate('/app/members/my-leaves/new')}>Apply</Button>
                    <Anchor size="sm" onClick={() => navigate('/app/members/my-leaves')}>View all</Anchor>
                  </Group>
                </Group>
                {recentLeaves.length === 0 ? (
                  <Text size="sm" c="dimmed">You haven't applied for any leave yet.</Text>
                ) : (
                  <Table verticalSpacing="sm" highlightOnHover>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Type</Table.Th>
                        <Table.Th>Dates</Table.Th>
                        <Table.Th>Days</Table.Th>
                        <Table.Th>Status</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {recentLeaves.map((l: any) => (
                        <Table.Tr
                          key={l.leaveID}
                          style={{ cursor: 'pointer' }}
                          onClick={() => navigate(`/app/members/my-leaves/${l.leaveID}`)}
                        >
                          <Table.Td>{l.typeName ?? 'Leave'}</Table.Td>
                          <Table.Td>{formatDate(l.startDate)} → {formatDate(l.endDate)}</Table.Td>
                          <Table.Td>{l.numberOfDays}</Table.Td>
                          <Table.Td>
                            <Badge color={l.statusColor ?? 'gray'} variant="light">
                              {l.statusName ?? '—'}
                            </Badge>
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                )}
              </Card>
            </Stack>
          </Grid.Col>

          {/* Right 1/3: profile, quick actions, upcoming public holidays */}
          <Grid.Col span={{ base: 12, md: 4 }}>
            <Stack gap="md">
              <ProfileSnapshotCard />
              <div data-help-id="hr.dashboard.quick-actions">
                <QuickActionsPanel />
              </div>

              {/* Upcoming public holidays */}
              <Card withBorder padding="lg" radius="md" data-help-id="hr.dashboard.holidays">
                <Group gap="xs" mb="sm">
                  <ThemeIcon color="orange" variant="light" radius="xl" size="sm">
                    <IconCalendar size={14} />
                  </ThemeIcon>
                  <Title order={5}>Upcoming Public Holidays</Title>
                </Group>
                {upcomingHolidays.length === 0 ? (
                  <Text size="sm" c="dimmed">No upcoming holidays in the catalogue.</Text>
                ) : (
                  <Stack gap={4}>
                    {upcomingHolidays.map((h: any) => {
                      const days = Math.max(
                        0,
                        Math.ceil((new Date(h.date).getTime() - Date.now()) / (24 * 60 * 60 * 1000))
                      );
                      return (
                        <Group key={h.holidayID} justify="space-between">
                          <Text size="sm">{h.name}</Text>
                          <Text size="xs" c="dimmed">
                            {days === 0 ? 'today' : `in ${days}d`}
                          </Text>
                        </Group>
                      );
                    })}
                  </Stack>
                )}
              </Card>
            </Stack>
          </Grid.Col>
        </Grid>
      </Stack>
    </FadeIn>
  );
}

interface StatTileProps {
  label: string;
  value: string;
  icon: React.ReactNode;
  color: string;
  onClick?: () => void;
  hint?: string;
}

function StatTile({ label, value, icon, color, onClick, hint }: StatTileProps) {
  return (
    <Card withBorder padding="lg" radius="md" style={{ cursor: onClick ? 'pointer' : undefined }} onClick={onClick}>
      <Group justify="space-between" mb="xs">
        <ThemeIcon color={color} variant="light" radius="xl" size="md">
          {icon}
        </ThemeIcon>
        {hint && <Text size="xs" c="dimmed">{hint}</Text>}
      </Group>
      <Text size="xl" fw={700}>{value}</Text>
      <Text size="xs" c="dimmed">{label}</Text>
    </Card>
  );
}

interface InfoTileProps {
  label: string;
  icon: React.ReactNode;
  color: string;
  onClick?: () => void;
  primary?: string;
  secondary?: string;
  badge?: string;
}

/**
 * Title-on-top tile for non-numeric stats (Leave Balances note, next
 * Important Date). Mirrors the live dashboard's richer corner tiles.
 */
function InfoTile({ label, icon, color, onClick, primary, secondary, badge }: InfoTileProps) {
  return (
    <Card withBorder padding="lg" radius="md" style={{ cursor: onClick ? 'pointer' : undefined }} onClick={onClick}>
      <Group justify="space-between" mb="xs" wrap="nowrap">
        <Group gap="xs" wrap="nowrap">
          <ThemeIcon color={color} variant="light" radius="xl" size="md">
            {icon}
          </ThemeIcon>
          <Text size="xs" fw={600} tt="uppercase" c="dimmed">{label}</Text>
        </Group>
        {badge && (
          <Badge size="sm" variant="light" color={color}>{badge}</Badge>
        )}
      </Group>
      {primary && <Text size="sm" fw={600} truncate="end">{primary}</Text>}
      {secondary && <Text size="xs" c="dimmed">{secondary}</Text>}
    </Card>
  );
}
